import React, { memo, useCallback } from 'react';
import { FormControl, FormHelperText, InputLabel, Select } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { ellipsis } from 'src/utils/StringUtils';
import useDealsFetcher from './hooks/useDealsFetcher';

interface Props {
  className?: string;
  domainId?: number;
  label: string;
  value: number;
  helperText?: string;
  error?: boolean;
  onChange: (value: number) => void;
}

const DEALS_LIMIT = 50;

const DealSelect = React.forwardRef<any, Props>((props, ref) => {
  const { className, domainId, label, onChange, value, helperText, ...otherProps } = props;

  const { loading, error, total, deals } = useDealsFetcher(DEALS_LIMIT, domainId);

  const hasValueInDealsList = useCallback((): boolean => {
    if (!value) {
      return true;
    }

    return deals.filter((deal) => deal.id === value).length > 0;
  }, [value, deals]);

  const handleChange = (event) => {
    const value = event.target.value;
    onChange(parseInt(value, 10));
  };

  if (loading) {
    return <Skeleton height={50} />;
  }

  if (error) {
    return <div style={{ color: '#f44336' }}>{error}</div>;
  }

  return (
    <FormControl ref={ref} className={className} variant='outlined' fullWidth {...otherProps}>
      <InputLabel>{label}</InputLabel>
      <Select native value={value || ''} onChange={handleChange} label={label}>
        <option value='' />
        {deals.map((deal, key) => (
          <option key={key} value={deal.id}>
            {`${deal.id} - ${ellipsis(deal.name, 40)}`}
          </option>
        ))}

        {!hasValueInDealsList() && <option value={value}>{value}</option>}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      {total > DEALS_LIMIT && (
        <FormHelperText>Seuls les {DEALS_LIMIT} premières affaires sont affichées</FormHelperText>
      )}
    </FormControl>
  );
});

export default memo(DealSelect);
